import * as React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeAction,
  MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import {
  Box,
  HeadingText,
  ParagraphText,
  StackLayout,
  GDSHeadingLevel,
  GDSHeadingSize,
} from '@leagueplatform/genesis-core';
import { useUniqueId } from '@leagueplatform/web-common';
import { Accordion as AccordionComponent } from '@leagueplatform/genesis-core-alpha';
import {
  addToActionAnalytics,
  useInvokeAction,
} from '@leagueplatform/masonry-renderers';
import { ACCORDION_ANALYTICS_DETAIL } from '#constants/masonry.constants.ts';
import { CoverageDetailsWidgetCard as CoverageDetailsWidgetCardType } from '#types/coverage-detail-widget-cards.types.ts';
import { CoverageDetailsWidgetCard } from './components/coverage-details-widget-card.component';
import {
  SectionComponent,
  type Section,
} from './components/coverage-details-widget-section.component';

export const NODE_TYPE_COVERAGE_DETAILS_WIDGET = 'coverageDetailsWidget';

interface Accordion {
  isExpanded?: boolean;
  id?: string;
  dividerPosition?: 'top' | 'bottom' | 'both' | 'none';
}

type CoverageDetailsProps = {
  heading?: string;
  headingLevel?: GDSHeadingLevel;
  headingSize?: GDSHeadingSize;
  accordion?: Accordion;
  card?: CoverageDetailsWidgetCardType;
  trailingCard?: CoverageDetailsWidgetCardType;
  sections?: Section[];
  description?: string;
};

export type CoverageDetailsNodeActions = {
  onAccordionClick?: MasonryEngineNodeAction;
  onCardClick?: MasonryEngineNodeAction;
  onTrailingCardClick?: MasonryEngineNodeAction;
  onLoad?: MasonryEngineNodeAction;
};

type CoverageDetailsNode = MasonryEngineNode<
  typeof NODE_TYPE_COVERAGE_DETAILS_WIDGET,
  CoverageDetailsProps,
  CoverageDetailsNodeActions,
  {}
>;

export type CoverageDetailsWidgetRendererProps =
  MasonryEngineNodeRendererProps<CoverageDetailsNode>;

export function CoverageDetailsWidgetRenderer({
  heading,
  headingLevel = '2',
  headingSize = 'lg',
  card,
  trailingCard,
  description,
  sections = [],
  onCardClick,
  onTrailingCardClick,
  onAccordionClick,
  nodeId,
  accordion,
}: CoverageDetailsWidgetRendererProps) {
  const sectionId = useUniqueId({ prefix: 'coverage-details-section-' });
  const headingId = accordion?.id ?? `${sectionId}-heading`;
  const invokeAction = useInvokeAction();
  const handleAccordionClick = React.useCallback(
    (accordionId: string, accordionHeader?: string) => {
      // Get current state for the accordion
      const accordionState = !accordion?.isExpanded;

      invokeAction({
        type: 'setNodeState',
        namespace: 'default',
        payload: {
          nodeId,
          state: {
            accordion: {
              ...accordion,
              isExpanded: accordionState,
            },
          },
        },
      });

      if (onAccordionClick) {
        invokeAction({
          ...addToActionAnalytics(onAccordionClick, {
            detail: accordionState
              ? ACCORDION_ANALYTICS_DETAIL.EXPAND_SERVICE
              : ACCORDION_ANALYTICS_DETAIL.COLLAPSE_SERVICE,
            content: {
              id: accordionId,
              name: accordionHeader ?? accordionId,
            },
          }),
          payload: {
            accordionId,
            isExpanded: accordionState,
          },
        });
      }
    },
    [accordion, invokeAction, nodeId, onAccordionClick],
  );

  const handleSubAccordionClick = React.useCallback(
    (sectionIndex: number, newAccordionState: boolean) => {
      const accordionId = sections[sectionIndex]?.accordion?.id;
      const accordionHeader = sections[sectionIndex]?.heading;
      // Update the section at the specified index
      const updatedSections = sections.map((section, index) => {
        if (index === sectionIndex) {
          return {
            ...section,
            accordion: {
              ...section.accordion,
              isExpanded: newAccordionState,
            },
          };
        }
        return section;
      });

      invokeAction({
        type: 'setNodeState',
        namespace: 'default',
        payload: {
          nodeId,
          state: {
            sections: updatedSections,
          },
        },
      });

      if (onAccordionClick && accordionId) {
        invokeAction({
          ...addToActionAnalytics(onAccordionClick, {
            detail: newAccordionState
              ? ACCORDION_ANALYTICS_DETAIL.EXPAND_SERVICE
              : ACCORDION_ANALYTICS_DETAIL.COLLAPSE_SERVICE,
            content: {
              id: accordionId,
              name: accordionHeader ?? accordionId,
            },
          }),
          payload: {
            accordionId,
            isExpanded: newAccordionState,
          },
        });
      }
    },
    [sections, invokeAction, nodeId, onAccordionClick],
  );
  const handleCardClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      if (onCardClick) {
        invokeAction({
          event,
          ...onCardClick,
        });
      }
    },
    [onCardClick, invokeAction],
  );

  const handleTrailingCardClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      if (onTrailingCardClick) {
        invokeAction({
          event,
          ...onTrailingCardClick,
        });
      }
    },
    [onTrailingCardClick, invokeAction],
  );

  if (!sections.length && !heading && !card) {
    return null;
  }

  const hasSubsectionAccordion = sections.some((section) => section.accordion);
  const hasHeaderSection = !!heading || !!description || !!card;
  const isHeaderSectionAccordion = accordion && heading;

  const mainContent = (
    <StackLayout
      css={{ width: '100%' }}
      spacing={hasSubsectionAccordion ? '$none' : '$two'}
    >
      {sections.map((section, index) => {
        const key = section.heading ?? `section-${index}`;
        return (
          <SectionComponent
            key={`section-${key}`}
            section={section}
            sectionIndex={index}
            onClick={handleSubAccordionClick}
          />
        );
      })}
    </StackLayout>
  );

  if (isHeaderSectionAccordion) {
    return (
      <Box as="section" css={{ width: '100%' }}>
        <AccordionComponent
          heading={heading}
          headingLevel={headingLevel}
          headingSize={headingSize}
          defaultExpanded={accordion?.isExpanded ?? false}
          isExpanded={accordion?.isExpanded}
          css={{
            width: '100%',
            marginBlockStart: '$none',
            borderBlockColor: '$onSurfaceBorderSubdued',
          }}
          id={headingId}
          onClick={() => handleAccordionClick(headingId, heading)}
          dividerPosition={accordion?.dividerPosition}
        >
          <StackLayout css={{ width: '100%' }} spacing="$oneAndHalf">
            {description && <ParagraphText>{description}</ParagraphText>}
            {card && (
              <CoverageDetailsWidgetCard
                card={card}
                headingId={card.cta?.isCtaGeneric ? headingId : undefined}
                onClick={handleCardClick}
                href={onCardClick?.payload.url as string}
              />
            )}
            {mainContent}
            {trailingCard && (
              <CoverageDetailsWidgetCard
                card={trailingCard}
                headingId={
                  trailingCard.cta?.isCtaGeneric ? headingId : undefined
                }
                onClick={handleTrailingCardClick}
                href={onTrailingCardClick?.payload.url as string}
              />
            )}
          </StackLayout>
        </AccordionComponent>
      </Box>
    );
  }

  return (
    <StackLayout as="section" spacing="$twoAndHalf">
      {hasHeaderSection && (
        <StackLayout spacing="$oneAndHalf" css={{ width: '100%' }}>
          {heading && (
            <HeadingText level={headingLevel} size={headingSize} id={headingId}>
              {heading}
            </HeadingText>
          )}
          {description && <ParagraphText>{description}</ParagraphText>}
          {card && (
            <CoverageDetailsWidgetCard
              card={card}
              headingId={card.cta?.isCtaGeneric ? headingId : undefined}
              href={onCardClick?.payload.url as string}
              onClick={handleCardClick}
            />
          )}
        </StackLayout>
      )}
      {mainContent}
      {trailingCard && (
        <CoverageDetailsWidgetCard
          card={trailingCard}
          headingId={trailingCard.cta?.isCtaGeneric ? headingId : undefined}
          href={onTrailingCardClick?.payload.url as string}
          onClick={handleTrailingCardClick}
        />
      )}
    </StackLayout>
  );
}
